<template>
  <PageHeaderComponent
    :pageHeading="$t('concepts_view.header')"
    filterVariant="gradeSubject"
    :includeAll="false"
  />

  <!-- Title-row counter: % of filtered students with no baseline assessment -->
  <section class="text-base pt-6">
    <div
      class="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700"
    >
      <i class="inline-flex items-center justify-center h-4 w-4 flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4">
        <rl-icon :icon="'rl-info-circle'" :title="'baseline'" aria-hidden="true" />
      </i>
      {{ baselineIncompletePct }}% {{ $t("concepts_view.baseline_incomplete") }}
      <span class="text-amber-600">({{ threadBaselineIncomplete.notCompleted }}/{{ threadBaselineIncomplete.total }})</span>
    </div>
  </section>

  <section class="text-base pt-6">
    <LoadingSpinnerComponent />
    <FiltersOutOfDateComponent />

    <div
      v-if="conceptsStore.loader.fetchConceptsOverview === false"
      class="flex flex-col xl:flex-row gap-5"
    >
      <!-- Left: per-concept stacked bars -->
      <div class="w-full xl:w-2/3 bg-white rounded-2xl p-5 border border-[#EAECF0]">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 class="text-primary font-semibold text-lg flex items-center gap-1.5">
            {{ $t("concepts_view.proficiency_by_concept") }}
            <i
              v-tooltip:bottom.tooltip="$t('concepts_view.tooltips.proficiency_by_concept')"
              class="relative inline-flex items-center justify-center h-4 w-4 flex-shrink-0 cursor-help text-tertiary [&>svg]:h-4 [&>svg]:w-4"
            >
              <rl-icon :icon="'rl-info-circle'" :title="'info'" aria-hidden="true" />
            </i>
          </h2>

          <select
            v-if="threads.length > 0"
            v-model="selectedThreadId"
            class="block rounded-md border-0 py-1.5 text-primary shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
          >
            <option v-for="thread in threads" :key="thread.threadId" :value="thread.threadId">
              {{ thread.threadName }}
            </option>
          </select>
        </div>

        <!-- Baseline / Latest toggle + students-to-be-assessed -->
        <div class="flex flex-wrap items-center gap-3 mb-5">
          <div class="inline-flex rounded-lg bg-gray-100 p-1">
            <button
              type="button"
              :class="[
                'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                mode === 'baseline' ? 'bg-white text-primary shadow-sm' : 'text-tertiary',
              ]"
              @click="mode = 'baseline'"
            >
              {{ $t("concepts_view.toggle.start") }} {{ Math.round(overallPct.baseline) }}%
            </button>
            <button
              type="button"
              :class="[
                'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                mode === 'latest' ? 'bg-white text-primary shadow-sm' : 'text-tertiary',
              ]"
              @click="mode = 'latest'"
            >
              {{ $t("concepts_view.toggle.current") }} {{ Math.round(overallPct.latest) }}%
            </button>
          </div>
          <span class="text-sm text-tertiary">
            {{ threadBaselineIncomplete.notCompleted }} {{ $t("concepts_view.to_be_assessed") }}
          </span>
          <i
            v-tooltip:bottom.tooltip="$t('concepts_view.tooltips.mastery_toggle')"
            class="relative inline-flex items-center justify-center h-4 w-4 flex-shrink-0 cursor-help text-tertiary [&>svg]:h-4 [&>svg]:w-4"
          >
            <rl-icon :icon="'rl-info-circle'" :title="'info'" aria-hidden="true" />
          </i>
        </div>

        <ConceptThreadBarsComponent
          v-if="bars.length > 0"
          :bars="bars"
          :segments="segments"
          @selectSegment="onSelectSegment"
        />
        <div v-else class="py-10 text-center text-sm text-tertiary">
          {{ $t("concepts_view.no_data") }}
        </div>
      </div>

      <!-- Right: Mastery at required level table -->
      <div class="w-full xl:w-1/3 bg-white rounded-2xl border border-[#EAECF0] overflow-hidden">
        <div class="px-6 py-5 border-b border-[#EAECF0]">
          <h2 class="text-primary font-semibold text-lg flex items-center gap-1.5">
            {{ $t("concepts_view.mastery_at_required_level") }}
            <i
              v-tooltip:bottom.tooltip="$t('concepts_view.tooltips.mastery_at_required_level')"
              class="relative inline-flex items-center justify-center h-4 w-4 flex-shrink-0 cursor-help text-tertiary [&>svg]:h-4 [&>svg]:w-4"
            >
              <rl-icon :icon="'rl-info-circle'" :title="'info'" aria-hidden="true" />
            </i>
          </h2>
        </div>
        <ConceptMasteryTableComponent :rows="masteryRows" />
      </div>
    </div>
  </section>

  <!-- Segment click-through modal -->
  <modal
    :showCloseButton="true"
    :widthClass="'max-w-[640px]'"
    :open="showModal"
    :onClose="closeModal"
    v-on:local-modal-open="showModal = $event"
  >
    <ConceptSegmentModalComponent
      :conceptTitle="modalConcept.title"
      :levelLabel="modalConcept.levelLabel"
      :levelPercentage="modalConcept.levelPercentage"
      @close="closeModal"
    />
  </modal>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import PageHeaderComponent from "@/components/global/PageHeaderComponent.vue";
import FiltersOutOfDateComponent from "@/modules/main/components/FiltersOutOfDateComponent.vue";
import LoadingSpinnerComponent from "@/modules/main/components/LoadingSpinnerComponent.vue";
import ConceptThreadBarsComponent from "@/modules/main/components/ConceptThreadBarsComponent.vue";
import ConceptMasteryTableComponent from "@/modules/main/components/ConceptMasteryTableComponent.vue";
import ConceptSegmentModalComponent from "@/modules/main/components/ConceptSegmentModalComponent.vue";
import { useConceptsStore } from "@/stores/conceptsStore";
import { useFilterStore } from "@/stores/filterStore";
import { useDataStore } from "@/stores/dataStore";
import {
  TOP_LEVEL,
  buildSegments,
  computeThreadBaselineIncomplete,
} from "@/modules/main/utils/conceptProficiency";

const conceptsStore = useConceptsStore();
const filterStore = useFilterStore();
const dataStore = useDataStore();

const selectedThreadId = ref(null);
const mode = ref("latest"); // 'baseline' | 'latest'

const showModal = ref(false);
const modalConcept = ref({ title: "", levelLabel: "", levelPercentage: null });

const overview = computed(() => conceptsStore.getConceptsOverview);

// Cohort is grade-scoped, normally a single assessmentType group; pick the largest.
const group = computed(() => {
  const groups = overview.value?.byAssessmentType || [];
  if (groups.length === 0) return null;
  return [...groups].sort((a, b) => b.studentCount - a.studentCount)[0];
});

const baselineIncomplete = computed(
  () => overview.value?.baselineIncomplete || { total: 0, notCompleted: 0, percentage: 0 }
);

// Per-thread baseline-incomplete: students in the filter who have not done the
// SELECTED thread's baseline — so the counter updates as the journey changes.
// (`total` stays the whole filtered cohort; students with no submission at all
// are counted as not-completed for every thread.)
const threadBaselineIncomplete = computed(() =>
  computeThreadBaselineIncomplete(
    overview.value,
    selectedThreadId.value,
    baselineIncomplete.value.total
  )
);
const baselineIncompletePct = computed(() => Math.round(threadBaselineIncomplete.value.percentage || 0));

// Universal 0–3 level scale; level 3 ("Expected") is always the required level.
const topLevel = TOP_LEVEL;

const segments = computed(() => buildSegments(group.value?.assessmentType));

const threads = computed(() => group.value?.threads || []);

const selectedThread = computed(
  () => threads.value.find((t) => t.threadId === selectedThreadId.value) || null
);

// Tally baseline levels per concept for the selected thread (from the mastery table).
const baselineCountsByConcept = computed(() => {
  const map = {};
  const tid = selectedThreadId.value;
  if (!group.value || tid == null) return map;
  for (const row of group.value.masteryTable) {
    for (const c of row.concepts) {
      if (c.threadId !== tid) continue;
      if (!map[c.conceptNumber]) map[c.conceptNumber] = { 0: 0, 1: 0, 2: 0, 3: 0 };
      if (c.baselineLevel === 0 || c.baselineLevel === 1 || c.baselineLevel === 2 || c.baselineLevel === 3) {
        map[c.conceptNumber][c.baselineLevel]++;
      }
    }
  }
  return map;
});

const assessedTotal = (counts) =>
  [0, 1, 2, 3].reduce((sum, l) => (l <= topLevel ? sum + (counts[l] || 0) : sum), 0);

// Bars for the selected thread + mode.
const bars = computed(() => {
  if (!selectedThread.value) return [];
  return selectedThread.value.concepts.map((concept) => {
    const counts =
      mode.value === "latest"
        ? concept.levelCounts
        : baselineCountsByConcept.value[concept.conceptNumber] || { 0: 0, 1: 0, 2: 0, 3: 0 };
    return {
      conceptNumber: concept.conceptNumber,
      title: concept.title,
      levelCounts: counts,
      assessedTotal: assessedTotal(counts),
    };
  });
});

// Mastery table rows: % of assessed students at the required (Expected/top) level.
const masteryRows = computed(() => {
  if (!selectedThread.value || !group.value) return [];
  const tid = selectedThreadId.value;
  const top = topLevel;

  // Per concept: count baseline/latest at top level and total assessed.
  const stats = {};
  for (const row of group.value.masteryTable) {
    for (const c of row.concepts) {
      if (c.threadId !== tid) continue;
      if (!stats[c.conceptNumber]) {
        stats[c.conceptNumber] = { baseTop: 0, baseTotal: 0, latestTop: 0, latestTotal: 0 };
      }
      const s = stats[c.conceptNumber];
      if (c.baselineLevel != null) {
        s.baseTotal++;
        if (c.baselineLevel === top) s.baseTop++;
      }
      if (c.latestLevel != null) {
        s.latestTotal++;
        if (c.latestLevel === top) s.latestTop++;
      }
    }
  }

  return selectedThread.value.concepts.map((concept) => {
    const s = stats[concept.conceptNumber] || { baseTop: 0, baseTotal: 0, latestTop: 0, latestTotal: 0 };
    return {
      conceptNumber: concept.conceptNumber,
      title: concept.title,
      startPct: s.baseTotal > 0 ? (s.baseTop / s.baseTotal) * 100 : null,
      currentPct: s.latestTotal > 0 ? (s.latestTop / s.latestTotal) * 100 : null,
    };
  });
});

// Headline % at Expected across the selected thread (for the toggle buttons).
const overallPct = computed(() => {
  const rows = masteryRows.value;
  const avg = (key) => {
    const vals = rows.map((r) => r[key]).filter((v) => v !== null);
    if (vals.length === 0) return 0;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };
  return { baseline: avg("startPct"), latest: avg("currentPct") };
});

function onSelectSegment({ conceptNumber, level }) {
  const concept = selectedThread.value?.concepts.find((c) => c.conceptNumber === conceptNumber);
  const segment = segments.value.find((s) => s.level === level);
  const bar = bars.value.find((b) => b.conceptNumber === conceptNumber);
  const pct =
    bar && bar.assessedTotal > 0 ? ((bar.levelCounts[level] || 0) / bar.assessedTotal) * 100 : null;

  modalConcept.value = {
    title: concept?.title || `Concept ${conceptNumber}`,
    levelLabel: segment?.label || "",
    levelPercentage: pct,
  };
  showModal.value = true;

  fetchConceptStudents(conceptNumber, level);
}

async function fetchConceptStudents(conceptNumber, level) {
  const payload = await dataStore.buildPayload();
  conceptsStore.fetchConceptStudents({
    ...payload,
    assessmentType: group.value?.assessmentType,
    threadId: selectedThreadId.value,
    conceptNumber,
    level,
  });
}

function closeModal() {
  showModal.value = false;
}

// Default the selected thread to the first thread that has concept data.
function resetSelectedThread() {
  const list = threads.value;
  if (list.length === 0) {
    selectedThreadId.value = null;
    return;
  }
  const stillValid = list.some((t) => t.threadId === selectedThreadId.value);
  if (!stillValid) {
    const withData = list.find((t) => t.concepts.length > 0);
    selectedThreadId.value = (withData || list[0]).threadId;
  }
}

watch(threads, resetSelectedThread, { immediate: true });
watch(
  () => filterStore.selectedSubject,
  () => {
    selectedThreadId.value = null;
    resetSelectedThread();
  }
);

onMounted(async () => {
  if (!overview.value && filterStore.selectedFilters.school) {
    const payload = await dataStore.buildPayload();
    conceptsStore.fetchConceptsOverview(payload);
  }
});
</script>
